import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, MapPin, Home, Building2, Check, Star } from "lucide-react";
import { AccountLayout } from "@/components/account/AccountLayout";
import { mockAddresses } from "@/lib/mock-account";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/account/addresses")({
  head: () => ({
    meta: [
      { title: "Address Book — Royal Lumina" },
      { name: "description", content: "Manage your delivery addresses for seamless checkout." },
    ],
  }),
  component: AddressesPage,
});

type Address = (typeof mockAddresses)[number];

const empty: Address = {
  id: "",
  label: "Home",
  name: "",
  line1: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  default: false,
};

function AddressesPage() {
  const [addresses, setAddresses] = useState<Address[]>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("rl_addresses_v1");
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          // ignore
        }
      }
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem("rl_addresses_v1", JSON.stringify(addresses));
  }, [addresses]);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Address>(empty);
  const [editingId, setEditingId] = useState<string | null>(null);

  const generateId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return "addr_" + Date.now().toString(36) + "_" + Math.random().toString(36).substring(2, 9);
  };

  const openAdd = () => {
    setDraft({ ...empty, id: generateId() });
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (a: Address) => {
    setDraft(a);
    setEditingId(a.id);
    setOpen(true);
  };

  const remove = (id: string) => {
    setAddresses((xs) => xs.filter((x) => x.id !== id));
    toast.success("Address removed from address book");
  };

  const setDefault = (id: string) => {
    setAddresses((xs) =>
      xs.map((x) => ({ ...x, default: x.id === id }))
    );
    toast.success("Default address updated");
  };

  const save = () => {
    if (!draft.name || !draft.line1 || !draft.city || !draft.zip || !draft.phone) {
      toast.error("Please fill all required fields");
      return;
    }

    setAddresses((xs) => {
      let updated = editingId ? xs.map((x) => (x.id === editingId ? draft : x)) : [...xs, draft];
      if (draft.default) {
        updated = updated.map((x) => ({ ...x, default: x.id === draft.id }));
      }
      return updated;
    });

    toast.success(editingId ? "Address updated successfully" : "Address added to address book");
    setOpen(false);
  };

  const set = <K extends keyof Address>(k: K, v: Address[K]) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <AccountLayout title="Address Book" subtitle="Manage your delivery destinations for quick, 1-click candle rituals checkout.">
      <div className="grid md:grid-cols-2 gap-6">
        {addresses.map((a) => (
          <div
            key={a.id}
            className={`bg-card border rounded-[22px] p-4 sm:p-6 shadow-luxe relative flex flex-col justify-between transition-all ${
              a.default ? "border-gold/60 ring-1 ring-gold/20" : "border-border/80 hover:border-gold/40"
            }`}
          >
            <div>
              <div className="flex items-center justify-between border-b border-border/60 pb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gold/10 grid place-items-center text-gold shrink-0">
                    {a.label.toLowerCase().includes("office") || a.label.toLowerCase().includes("work") ? (
                      <Building2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    ) : (
                      <Home className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    )}
                  </div>
                  <span className="text-[0.65rem] font-semibold tracking-[0.2em] uppercase text-gold truncate">
                    {a.label}
                  </span>
                </div>

                {a.default ? (
                  <span className="text-[0.58rem] sm:text-[0.6rem] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-primary text-gold border border-gold/30 flex items-center gap-1 shrink-0">
                    <Star className="h-3 w-3 fill-gold" /> Default
                  </span>
                ) : (
                  <button
                    onClick={() => setDefault(a.id)}
                    className="text-[0.65rem] font-medium text-muted-foreground hover:text-gold shrink-0 whitespace-nowrap"
                  >
                    Set as default
                  </button>
                )}
              </div>

              <div className="mt-4 space-y-1">
                <h4 className="font-display font-bold text-xl text-primary">{a.name}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed pt-1">
                  {a.line1}<br />
                  {a.city}, {a.state} {a.zip}<br />
                  <span className="font-mono text-primary/80 font-medium block mt-1">{a.phone}</span>
                </p>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between">
              <button
                onClick={() => openEdit(a)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-gold transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit Details
              </button>

              {!a.default && (
                <button
                  onClick={() => remove(a.id)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Add New Address Card */}
        <button
          onClick={openAdd}
          className="min-h-[220px] border-2 border-dashed border-border/80 rounded-[22px] grid place-items-center hover:border-gold hover:bg-gold/5 transition-all group p-6"
        >
          <div className="text-center space-y-3">
            <div className="mx-auto h-12 w-12 rounded-full bg-secondary grid place-items-center group-hover:bg-gold/20 transition-colors">
              <Plus className="h-6 w-6 text-gold" />
            </div>
            <div>
              <div className="font-display font-bold text-lg text-primary">Add New Delivery Address</div>
              <p className="text-xs text-muted-foreground mt-0.5">Save a new home, office, or gift destination</p>
            </div>
          </div>
        </button>
      </div>

      {/* Modal Dialog for Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-card border border-border rounded-[24px] p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold text-primary">
              {editingId ? "Edit Delivery Address" : "Add New Delivery Address"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Address Label</Label>
                <Input value={draft.label} onChange={(e) => set("label", e.target.value)} placeholder="Home / Office / Gift" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Full Name *</Label>
                <Input value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="Recipient name" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Street & Flat / Building *</Label>
              <Input value={draft.line1} onChange={(e) => set("line1", e.target.value)} placeholder="House No., Street Name, Area" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">City *</Label>
                <Input value={draft.city} onChange={(e) => set("city", e.target.value)} placeholder="Mumbai" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">State *</Label>
                <Input value={draft.state} onChange={(e) => set("state", e.target.value)} placeholder="Maharashtra" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PIN Code *</Label>
                <Input value={draft.zip} onChange={(e) => set("zip", e.target.value)} placeholder="400001" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone Number *</Label>
              <Input value={draft.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" />
            </div>

            <label className="flex items-center gap-2.5 text-xs font-medium text-primary cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={draft.default}
                onChange={(e) => set("default", e.target.checked)}
                className="rounded border-border accent-gold"
              />
              Set as my default shipping address
            </label>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="h-10 text-xs">
              Cancel
            </Button>
            <Button onClick={save} className="btn-gold h-10 px-6 text-xs uppercase tracking-wider font-semibold">
              {editingId ? "Save Changes" : "Save Address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountLayout>
  );
}
